import React from "react";

import { useTranslation } from "react-i18next";
import { ColumnStyles } from "../columnsAndActions/columnStyles";
import CustomFilterHeader from "../customFilterHeader/CustomFilterHeader";
import { CustomColumnHeaderStyles } from "./CustomColumnHeaderStyles";
import { InarixGridFilterModel } from "../../../../redux/slices/dataTable/data";

import { GridSortModel } from "@mui/x-data-grid-pro";
import { Tooltip, Menu, MenuItem } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PushPinIcon from "@mui/icons-material/PushPin";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";

interface CustomColumnHeaderProps {
  headerName: string;
  field: string;
  allColumns: any[];
  filterModel: InarixGridFilterModel;
  sortModel?: GridSortModel;
  onToggleVisibility: (field: string) => void;
  setPinnedColumns: (pinnedColumns: {
    left: string[];
    right: string[];
  }) => void;
  pinnedColumns: {
    left: string[];
    right: string[];
  };
  columnType?: string;
  onOpenFilter?: (field: string) => void;
  availableFilters?: string[];
  onSort?: (field: string, direction: "asc" | "desc" | null) => void;
  sortable?: boolean;
}

const CustomColumnHeader: React.FC<CustomColumnHeaderProps> = ({
  headerName,
  field,
  allColumns,
  onToggleVisibility,
  setPinnedColumns,
  pinnedColumns,
  columnType,
  onOpenFilter,
  availableFilters,
  onSort,
  sortable = false,
  filterModel,
  sortModel,
}) => {
  const [t] = useTranslation("global");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Count filters for this specific column
  const columnFiltersCount =
    filterModel?.items?.filter(
      (item) => item.field === field && item.operator && item.value,
    ).length || 0;

  const getColumnTypeDescription = (type?: string) => {
    switch (type) {
      case "measure":
        return t("Data.SwitchesValues.measure");
      case "exported-measure":
        return t("Data.SwitchesValues.measure");
      case "label":
        return t("Data.SwitchesValues.label");
      case "data":
        return t("Data.SwitchesValues.data");
      default:
        return "";
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (columnField: string) => {
    onToggleVisibility(columnField);
    handleClose();
  };

  const handleOpenFilter = (columnField: string) => {
    onOpenFilter?.(columnField);
    handleClose();
  };

  const handleSort = (direction: "asc" | "desc") => {
    onSort?.(field, direction);
    handleClose();
  };

  const handleClearSort = () => {
    onSort?.(field, null);
    handleClose();
  };

  const hasFilterOperators = () => {
    return (
      availableFilters &&
      availableFilters.length > 0 &&
      availableFilters.some((filter) => filter !== "")
    );
  };

  const getCurrentSortDirection = () => {
    if (sortModel && sortModel.length > 0) {
      const fieldSort = sortModel.find((sort) => sort.field === field);
      return fieldSort ? fieldSort.sort : null;
    }
    return null;
  };

  const isSortable = () => {
    return sortable === true && onSort !== undefined;
  };

  const handleToggleFreeze = (
    columnField: string,
    pinned: "left" | "right",
  ) => {
    if (pinned === "left") {
      if (pinnedColumns.left.includes(columnField)) {
        setPinnedColumns({
          ...pinnedColumns,
          left: pinnedColumns.left.filter((col) => col !== columnField),
        });
        return;
      }

      setPinnedColumns({
        ...pinnedColumns,
        left: [...pinnedColumns.left, columnField],
        right: pinnedColumns.right.filter((col) => col !== columnField),
      });
    } else {
      if (pinnedColumns.right.includes(columnField)) {
        setPinnedColumns({
          ...pinnedColumns,
          right: pinnedColumns.right.filter((col) => col !== columnField),
        });
        return;
      }
      setPinnedColumns({
        ...pinnedColumns,
        right: [...pinnedColumns.right, columnField],
        left: pinnedColumns.left.filter((col) => col !== columnField),
      });
    }
  };

  return (
    <div style={CustomColumnHeaderStyles.container}>
      <div style={CustomColumnHeaderStyles.contentContainer}>
        <div
          style={CustomColumnHeaderStyles.hoverContainer}
          onMouseEnter={(e) => {
            const sortButton = e.currentTarget.querySelector(".sort-button");
            if (sortButton && !getCurrentSortDirection()) {
              (sortButton as HTMLElement).style.opacity = "1";
            }
          }}
          onMouseLeave={(e) => {
            const sortButton = e.currentTarget.querySelector(".sort-button");
            if (sortButton && !getCurrentSortDirection()) {
              (sortButton as HTMLElement).style.opacity = "0";
            }
          }}
        >
          <Tooltip title={getColumnTypeDescription(columnType)} arrow>
            <div
              style={{
                ...ColumnStyles.headerText,
              }}
            >
              {headerName}
              <CustomFilterHeader field={field} filterModel={filterModel} />
            </div>
          </Tooltip>

          {onSort && isSortable() && (
            <button
              type="button"
              className="sort-button"
              aria-label="Toggle sort"
              onClick={(e) => {
                e.stopPropagation();
                const currentDirection = getCurrentSortDirection();
                if (currentDirection === null) {
                  onSort(field, "asc");
                } else if (currentDirection === "asc") {
                  onSort(field, "desc");
                } else {
                  // If desc, go to asc (no null state for the button)
                  onSort(field, "asc");
                }
              }}
              style={{
                ...CustomColumnHeaderStyles.sortButton,
                ...(getCurrentSortDirection()
                  ? CustomColumnHeaderStyles.sortButtonActive
                  : CustomColumnHeaderStyles.sortButtonInactive),
              }}
              onMouseEnter={(e) => {
                Object.assign(
                  e.currentTarget.style,
                  CustomColumnHeaderStyles.sortButtonHover,
                );
              }}
              onMouseLeave={(e) => {
                Object.assign(
                  e.currentTarget.style,
                  CustomColumnHeaderStyles.sortButtonDefault,
                );
              }}
            >
              {getCurrentSortDirection() === "asc" ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : getCurrentSortDirection() === "desc" ? (
                <ArrowDownwardIcon fontSize="small" />
              ) : (
                <ArrowUpwardIcon fontSize="small" />
              )}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label="Column options"
        onClick={handleClick}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        style={{
          background: "#ffff",
          ...ColumnStyles.headerMenuButton,
          ...CustomColumnHeaderStyles.menuButton,
        }}
      >
        <MoreVertIcon fontSize="small" />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onOpenFilter && hasFilterOperators() && field !== "createdAt" && (
          <MenuItem onClick={() => handleOpenFilter(field)} dense>
            <FilterAltIcon
              sx={{
                ...CustomColumnHeaderStyles.menuIcon,
                color:
                  columnFiltersCount > 0 ? "primary.main" : "text.disabled",
              }}
            />
            <span>{t("custom_filter.filter_column")}</span>
          </MenuItem>
        )}

        {onSort && isSortable() && (
          <>
            <MenuItem onClick={() => handleSort("asc")} dense>
              <ArrowUpwardIcon
                sx={{
                  ...CustomColumnHeaderStyles.menuIcon,
                  color:
                    getCurrentSortDirection() === "asc"
                      ? "primary.main"
                      : "text.disabled",
                }}
              />
              <span>{t("Data.SortAscending")}</span>
            </MenuItem>

            <MenuItem onClick={() => handleSort("desc")} dense>
              <ArrowDownwardIcon
                sx={{
                  ...CustomColumnHeaderStyles.menuIcon,
                  color:
                    getCurrentSortDirection() === "desc"
                      ? "primary.main"
                      : "text.disabled",
                }}
              />
              <span>{t("Data.SortDescending")}</span>
            </MenuItem>

            {getCurrentSortDirection() && (
              <MenuItem onClick={handleClearSort} dense>
                <SortIcon
                  sx={{
                    ...CustomColumnHeaderStyles.menuIcon,
                    color: "text.disabled",
                  }}
                />
                <span>{t("Data.ClearSort")}</span>
              </MenuItem>
            )}
          </>
        )}

        <MenuItem onClick={() => handleToggleColumn(field)} dense>
          <VisibilityIcon
            sx={{
              ...CustomColumnHeaderStyles.menuIcon,
              color: "primary.main",
            }}
          />
          <span> {t("Data.Hide")}</span>
        </MenuItem>

        <MenuItem onClick={() => handleToggleFreeze(field, "left")} dense>
          <PushPinIcon
            sx={{
              ...CustomColumnHeaderStyles.menuIcon,
              color: pinnedColumns.left.includes(field)
                ? "primary.main"
                : "text.disabled",
              transform: CustomColumnHeaderStyles.menuIconTransforms.pinLeft,
            }}
          />
          {pinnedColumns.left.includes(field)
            ? t("Data.unpinLeft")
            : t("Data.pinLeft")}
        </MenuItem>
        <MenuItem onClick={() => handleToggleFreeze(field, "right")} dense>
          <PushPinIcon
            sx={{
              ...CustomColumnHeaderStyles.menuIcon,
              color: pinnedColumns.right.includes(field)
                ? "primary.main"
                : "text.disabled",
              transform: CustomColumnHeaderStyles.menuIconTransforms.pinRight,
            }}
          />
          {pinnedColumns.right.includes(field)
            ? t("Data.unpinRight")
            : t("Data.pinRight")}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default CustomColumnHeader;
